import * as React from 'react';
import { useCallback, useContext, useState } from 'react';

import { useQuery } from '@apollo/client';

import type {
  GetAiGradingEligibilityByCourseQuery,
  GetAiGradingEligibilityByCourseQueryVariables,
} from 'bundles/peer/api/__generated__/getAiGradingEligibilityByCourse';
import getCourseAiGradingEligibilityQuery from 'bundles/peer/api/getAiGradingEligibilityByCourse.graphql';

export type CourseAiGradingContextType = {
  getItemAiGradingEligibility: (itemId: string) => boolean;
  isLoading: boolean;
};

// Components using this context are not owned by our team, and may be used
// outside of `<CourseAiGradingProvider>`. To gracefully degrade in such cases,
// our context supports default value.
const CourseAiGradingContext = React.createContext<CourseAiGradingContextType>({
  getItemAiGradingEligibility: () => false,
  isLoading: false,
});

export function useCourseAiGradingContext() {
  return useContext(CourseAiGradingContext);
}

export type CourseAiGradingProviderProps = {
  courseId: string;
  children: React.ReactNode;
};

const CourseAiGradingProvider = ({ courseId, children }: CourseAiGradingProviderProps) => {
  const [loading, setLoading] = useState(true);

  const [itemEligibility, setItemEligibility] = useState<Record<string, boolean>>({});

  const onCompleted = (data: GetAiGradingEligibilityByCourseQuery) => {
    setLoading(false);
    const itemsMap = data.PeerReviewAi?.getAiGradingEligibilityByCourse.reduce((acc, item) => {
      return { ...acc, [item.itemId]: item.isEligible };
    }, {});
    setItemEligibility(itemsMap || {});
  };

  useQuery<GetAiGradingEligibilityByCourseQuery, GetAiGradingEligibilityByCourseQueryVariables>(
    getCourseAiGradingEligibilityQuery,
    {
      variables: {
        courseId,
      },
      context: { clientName: 'gatewayGql' },
      onCompleted,
      onError: () => setLoading(false),
    }
  );

  const getItemAiGradingEligibility = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (itemId: string) => itemEligibility[itemId] || false,
    [itemEligibility]
  );

  const isLoading = loading;

  return (
    <CourseAiGradingContext.Provider
      value={{
        isLoading,
        getItemAiGradingEligibility,
      }}
    >
      {children}
    </CourseAiGradingContext.Provider>
  );
};

export default CourseAiGradingProvider;
